/**
 * Password Change Card
 *
 * FT-108: Self-service password change for users
 * Phase 6 (2026-02-11): Azure Migration Complete
 * FT-108 (2026-04-09): Wired to Azure auth change-password API
 *
 * Allows users to change their password with current password verification.
 * Calls POST /api/auth/change-password via azureApiClient.
 */

import React, { useState } from 'react';
import { Lock, Eye, EyeOff, CheckCircle, XCircle, ChevronDown, ChevronUp } from 'lucide-react';
import azureApiClient from '../../../lib/azureApiClient';
import { useCustomDialogs } from '../../../hooks/useCustomDialogs';

export const PasswordChangeCard = () => {
  const { showAlert, AlertDialog } = useCustomDialogs();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Show/hide password toggles
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Error state
  const [errors, setErrors] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Password strength calculation
  const calculatePasswordStrength = (password) => {
    if (!password) return { score: 0, label: '', color: '' };

    let score = 0;
    const checks = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };

    if (checks.length) score += 20;
    if (checks.uppercase) score += 20;
    if (checks.lowercase) score += 20;
    if (checks.number) score += 20;
    if (checks.special) score += 20;

    // Length bonus
    if (password.length >= 12) score += 10;
    if (password.length >= 16) score += 10;

    // Cap at 100
    score = Math.min(score, 100);

    let label = '';
    let color = '';

    if (score < 40) {
      label = 'Weak';
      color = 'text-red-600 bg-red-100';
    } else if (score < 70) {
      label = 'Fair';
      color = 'text-yellow-600 bg-yellow-100';
    } else if (score < 90) {
      label = 'Good';
      color = 'text-green-600 bg-green-100';
    } else {
      label = 'Strong';
      color = 'text-green-700 bg-green-200';
    }

    return { score, label, color, checks };
  };

  const passwordStrength = calculatePasswordStrength(newPassword);

  // Validate form
  const validateForm = () => {
    const newErrors = {
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    };

    if (!currentPassword) {
      newErrors.currentPassword = 'Current password is required';
    }

    if (!newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters';
    } else if (!/[A-Z]/.test(newPassword)) {
      newErrors.newPassword = 'Password must contain at least one uppercase letter';
    } else if (!/[a-z]/.test(newPassword)) {
      newErrors.newPassword = 'Password must contain at least one lowercase letter';
    } else if (!/[0-9]/.test(newPassword)) {
      newErrors.newPassword = 'Password must contain at least one number';
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your new password';
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return !newErrors.currentPassword && !newErrors.newPassword && !newErrors.confirmPassword;
  };

  // Handle form submission
  // FT-108: Wired to Azure auth change-password API
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // FT-108: Call Azure API to change password
      const result = await azureApiClient.changePassword(currentPassword, newPassword);

      if (!result.success) {
        // Handle specific error cases
        if (result.error?.toLowerCase().includes('current password is incorrect') ||
            result.error?.toLowerCase().includes('invalid password')) {
          setErrors({ ...errors, currentPassword: 'Current password is incorrect' });
          showAlert('Password Change Failed', 'The current password you entered is incorrect', 'error');
        } else if (result.error?.toLowerCase().includes('same') ||
                   result.error?.toLowerCase().includes('different')) {
          setErrors({ ...errors, newPassword: 'New password must be different from current password' });
          showAlert('Password Change Failed', 'New password must be different from your current password', 'error');
        } else {
          showAlert('Password Change Failed', result.error || 'Failed to change password', 'error');
        }
        return;
      }

      // Success feedback
      showAlert('Password Changed', 'Your password has been changed successfully', 'success');

      // Reset form
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setErrors({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setIsExpanded(false); // Collapse card after success

    } catch (error) {
      console.error('Password change error:', error);
      showAlert('Password Change Failed', error.message || 'An unexpected error occurred', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setErrors({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setIsExpanded(false);
  };

  return (
    <>
    <AlertDialog />
    <div className="bg-white shadow rounded-lg">
      {/* Collapsed state - just header with expand button */}
      {!isExpanded ? (
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Lock className="h-8 w-8 text-cyan-600" />
              </div>
              <div className="ml-4">
                <h4 className="text-lg font-medium text-gray-900">Security</h4>
                <p className="text-sm text-gray-500">
                  Change your password
                </p>
              </div>
            </div>
            <button
              onClick={() => setIsExpanded(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-cyan-600 hover:bg-cyan-700"
            >
              <ChevronDown className="h-4 w-4 mr-1" />
              Change Password
            </button>
          </div>
        </div>
      ) : (
        /* Expanded state - full form */
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Lock className="h-8 w-8 text-cyan-600" />
              </div>
              <div className="ml-4">
                <h4 className="text-lg font-medium text-gray-900">Change Password</h4>
                <p className="text-sm text-gray-500">
                  Enter your current password and choose a new one
                </p>
              </div>
            </div>
            <button
              onClick={handleCancel}
              className="text-gray-400 hover:text-gray-600"
            >
              <ChevronUp className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Current Password */}
            <div>
              <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700">
                Current Password
              </label>
              <div className="mt-1 relative">
                <input
                  id="currentPassword"
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className={`block w-full px-3 py-2 border ${
                    errors.currentPassword ? 'border-red-300' : 'border-gray-300'
                  } rounded-md shadow-sm focus:outline-none focus:ring-cyan-500 focus:border-cyan-500`}
                  disabled={isSubmitting}
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showCurrentPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.currentPassword && (
                <p className="mt-1 text-sm text-red-600">{errors.currentPassword}</p>
              )}
            </div>

            {/* New Password */}
            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                New Password
              </label>
              <div className="mt-1 relative">
                <input
                  id="newPassword"
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className={`block w-full px-3 py-2 border ${
                    errors.newPassword ? 'border-red-300' : 'border-gray-300'
                  } rounded-md shadow-sm focus:outline-none focus:ring-cyan-500 focus:border-cyan-500`}
                  disabled={isSubmitting}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showNewPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.newPassword && (
                <p className="mt-1 text-sm text-red-600">{errors.newPassword}</p>
              )}

              {/* Password Strength Indicator */}
              {newPassword && (
                <div className="mt-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-600">Password strength:</span>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded ${passwordStrength.color}`}>
                      {passwordStrength.label}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                    <div
                      className={`h-1.5 rounded-full transition-all ${
                        passwordStrength.score < 40
                          ? 'bg-red-600'
                          : passwordStrength.score < 70
                          ? 'bg-yellow-600'
                          : 'bg-green-600'
                      }`}
                      style={{ width: `${passwordStrength.score}%` }}
                    ></div>
                  </div>

                  {/* Password Requirements Checklist */}
                  <div className="mt-2 space-y-1">
                    <div className="flex items-center text-xs">
                      {passwordStrength.checks?.length ? (
                        <CheckCircle className="h-3 w-3 text-green-600 mr-1" />
                      ) : (
                        <XCircle className="h-3 w-3 text-gray-400 mr-1" />
                      )}
                      <span className={passwordStrength.checks?.length ? 'text-green-600' : 'text-gray-500'}>
                        At least 8 characters
                      </span>
                    </div>
                    <div className="flex items-center text-xs">
                      {passwordStrength.checks?.uppercase ? (
                        <CheckCircle className="h-3 w-3 text-green-600 mr-1" />
                      ) : (
                        <XCircle className="h-3 w-3 text-gray-400 mr-1" />
                      )}
                      <span className={passwordStrength.checks?.uppercase ? 'text-green-600' : 'text-gray-500'}>
                        One uppercase letter
                      </span>
                    </div>
                    <div className="flex items-center text-xs">
                      {passwordStrength.checks?.lowercase ? (
                        <CheckCircle className="h-3 w-3 text-green-600 mr-1" />
                      ) : (
                        <XCircle className="h-3 w-3 text-gray-400 mr-1" />
                      )}
                      <span className={passwordStrength.checks?.lowercase ? 'text-green-600' : 'text-gray-500'}>
                        One lowercase letter
                      </span>
                    </div>
                    <div className="flex items-center text-xs">
                      {passwordStrength.checks?.number ? (
                        <CheckCircle className="h-3 w-3 text-green-600 mr-1" />
                      ) : (
                        <XCircle className="h-3 w-3 text-gray-400 mr-1" />
                      )}
                      <span className={passwordStrength.checks?.number ? 'text-green-600' : 'text-gray-500'}>
                        One number
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Confirm New Password
              </label>
              <div className="mt-1 relative">
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={`block w-full px-3 py-2 border ${
                    errors.confirmPassword ? 'border-red-300' : 'border-gray-300'
                  } rounded-md shadow-sm focus:outline-none focus:ring-cyan-500 focus:border-cyan-500`}
                  disabled={isSubmitting}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={handleCancel}
                disabled={isSubmitting}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50"
              >
                {isSubmitting ? 'Changing Password...' : 'Change Password'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
    </>
  );
};

export default PasswordChangeCard;
